<template>
  <div class="dash-modal-backdrop" @click.self="close">
    <div class="dash-modal dash-modal-lg lv-request-modal" role="dialog" :aria-label="title">
      <div class="dash-modal-header">
        <div style="min-width: 0;">
          <h6 class="dash-modal-title">{{ title }}</h6>
          <p class="dash-modal-subtitle">{{ subtitle }}</p>
        </div>
        <button type="button" class="side-drawer-close" aria-label="Close" @click="close"><i class="pi pi-times"></i></button>
      </div>

      <form novalidate @submit.prevent="save">
        <div class="dash-modal-body">
          <div class="lv-request-grid">
            <div class="lv-request-form">
              <div class="pay-field">
                <label class="filter-label">Employee <span class="req">*</span></label>
                <div v-if="employeeLocked && !employee" class="dash-skeleton" style="height: 50px; border-radius: 10px;"></div>
                <employeePicker v-else v-model="employee" :disabled="employeeLocked" :invalid="!!errors.employee" />
                <span v-if="errors.employee" class="field-error">{{ errors.employee }}</span>
              </div>

              <div class="pay-field">
                <label class="filter-label" for="lrType">Leave type <span class="req">*</span></label>
                <select id="lrType" v-model="form.leaveTypeId" :class="['form-control', 'filter-input', { invalid: errors.leaveTypeId }]" :disabled="typesLoading && !leaveTypes.length">
                  <option :value="null" disabled>{{ typesLoading && !leaveTypes.length ? 'Loading leave types' : 'Choose a leave type' }}</option>
                  <option v-for="type in leaveTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                </select>
                <span v-if="errors.leaveTypeId" class="field-error">{{ errors.leaveTypeId }}</span>
                <div v-else-if="typeInfo" :class="['lv-type-note', typeInfo.tone]">
                  <i :class="['pi', typeInfo.icon]"></i><span>{{ typeInfo.text }}</span>
                </div>
              </div>

              <div class="pay-form-grid">
                <div class="pay-field">
                  <label class="filter-label">First day <span class="req">*</span></label>
                  <div class="date-field"><calendarDatePicker v-model="form.startDate" /></div>
                  <span v-if="errors.startDate" class="field-error">{{ errors.startDate }}</span>
                </div>
                <div class="pay-field">
                  <label class="filter-label">Length</label>
                  <div class="pay-seg fill">
                    <button type="button" :class="{ active: !form.halfDay }" @click="form.halfDay = false">Full days</button>
                    <button type="button" :class="{ active: form.halfDay }" @click="setHalfDay">Half day</button>
                  </div>
                </div>
                <div v-if="!form.halfDay" class="pay-field">
                  <label class="filter-label" for="lrDays">Days of leave <span class="req">*</span></label>
                  <div class="pay-input-group">
                    <input id="lrDays" v-model.number="form.days" type="number" min="1" step="1" inputmode="numeric" :class="['form-control', 'filter-input', { invalid: errors.days }]" />
                    <span class="suffix">days</span>
                  </div>
                  <span v-if="errors.days" class="field-error">{{ errors.days }}</span>
                  <span v-else class="hint">Holidays and days off are not counted.</span>
                </div>
                <div v-else class="pay-field">
                  <label class="filter-label">Which half</label>
                  <div class="pay-seg fill">
                    <button type="button" :class="{ active: form.halfDaySlot === 'am' }" @click="form.halfDaySlot = 'am'">Morning</button>
                    <button type="button" :class="{ active: form.halfDaySlot === 'pm' }" @click="form.halfDaySlot = 'pm'">Afternoon</button>
                  </div>
                </div>
                <div class="pay-field">
                  <label class="filter-label">Last day</label>
                  <div class="lv-readonly">{{ endDate ? $filters.getShortDate(endDate) : 'Set the first day' }}</div>
                </div>
              </div>

              <div class="pay-field">
                <label class="filter-label" for="lrReason">Reason</label>
                <textarea id="lrReason" v-model="form.reason" class="form-control filter-input lv-textarea" rows="3" maxlength="500" placeholder="Optional. Anything the approver should know."></textarea>
              </div>

              <div v-if="overBalance" class="pay-notice warn">
                <i class="pi pi-exclamation-triangle"></i>
                <div class="body">This is {{ formatDays(overBalance) }} more than the available balance. It can still be submitted, but approving it takes the balance below zero.</div>
              </div>
            </div>

            <div class="lv-request-side">
              <div class="lv-mini-cal">
                <div class="lv-mini-cal-head">
                  <button type="button" class="dash-icon-btn" aria-label="Previous month" @click="monthOffset -= 1"><i class="pi pi-chevron-left"></i></button>
                  <span class="lv-mini-cal-title">{{ view.label }}</span>
                  <button type="button" class="dash-icon-btn" aria-label="Next month" @click="monthOffset += 1"><i class="pi pi-chevron-right"></i></button>
                </div>
                <div class="loading"><div v-show="holidaysLoading" class="loading-bar"></div></div>
                <div class="lv-mini-cal-grid">
                  <span v-for="day in WEEKDAY_SHORT" :key="day" class="lv-mini-cal-dow">{{ day }}</span>
                  <button v-for="cell in cells" :key="cell.key" type="button" :class="cellClass(cell)" :disabled="!cell.inMonth" :title="cell.title" :aria-label="cell.title || undefined" @click="toggleDay(cell)">
                    <span class="n">{{ cell.day }}</span>
                  </button>
                </div>
                <div class="lv-mini-cal-legend">
                  <span><i class="sw counted"></i>Leave</span>
                  <span><i class="sw off"></i>Not counted</span>
                  <span><i class="sw skipped"></i>Skipped</span>
                  <span><i class="sw holiday"></i>Holiday</span>
                </div>
              </div>

              <div class="lv-request-summary">
                <template v-if="form.startDate">
                  <div class="lv-request-summary-range">{{ rangeLabel }}</div>
                  <div class="lv-request-summary-length">{{ lengthLabel }}</div>
                </template>
                <div v-else class="lv-request-summary-length">Pick the first day, or click a day on the calendar.</div>
                <p v-if="form.startDate && !form.halfDay" class="hint">Click a day inside the leave to skip it. Click a holiday inside the leave to count it.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="dash-modal-footer">
          <button type="button" class="btn btn-custom-secondary" :disabled="saving" @click="close">Cancel</button>
          <button type="submit" class="btn btn-custom-primary" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-else>{{ isEdit ? 'Save changes' : isSelf ? 'Send request' : 'Submit request' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import employeePicker from '@/components/employee/Employee_Picker.vue';
import calendarDatePicker from '@/components/date/Calendar_Date_Picker.vue';
import Employee from '@/services/employee';
import { EDIT_LEAVE_REQUEST, GET_LEAVE_BALANCES, GET_LEAVE_TYPES, REGISTER_LEAVE_REQUEST } from '@/store/action_types';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';
import { CalendarSystem } from '@/utils/enums';
import { leaveEligibility } from '@/utils/employee';
import { fromDateKey, toDateKey } from '@/utils/attendance_dates';
import { addDaysToKey, countsAsLeave, dateKeyOf, fetchHolidaysBetween, formatDays, leaveEndDate, monthView, todayKey } from '@/utils/leave';

const WEEKDAY_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** "YYYY-MM" for every Gregorian month touched by an inclusive date-key range. */
const monthsBetween = (fromKey, toKey) => {
  const months = [];
  const cursor = fromDateKey(`${fromKey.slice(0, 7)}-01`);
  const stop = fromDateKey(`${toKey.slice(0, 7)}-01`);
  while (cursor <= stop && months.length < 24) {
    months.push(toDateKey(cursor).slice(0, 7));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
};

/**
 * Create or edit a leave request. Used through Add_/Edit_Leave_Request_Modal,
 * which keep the event names their call sites listen for.
 */
export default {
  name: 'LeaveRequestDialog',
  components: { employeePicker, calendarDatePicker },
  props: {
    mode: { type: String, default: 'create' },
    /** Locks the request to this employee (profile, employee record, dashboard). */
    employeeId: { type: String, default: null },
    /** The request being edited. */
    request: { type: Object, default: null },
  },
  emits: ['close', 'saved'],
  data() {
    const r = this.mode === 'edit' && this.request ? this.request : {};
    const halfDay = r.unit === 'halfDay' || Number(r.days) === 0.5;
    return {
      WEEKDAY_SHORT,
      employee: r.employee || null,
      form: {
        leaveTypeId: r.leaveTypeId || null,
        startDate: dateKeyOf(r.startDate) || null,
        days: halfDay ? 1 : Number(r.days) || 1,
        halfDay,
        halfDaySlot: r.halfDaySlot === 'pm' ? 'pm' : 'am',
        skipDays: (r.skipDays || []).map(dateKeyOf).filter(Boolean),
        includeDays: (r.includeDays || []).map(dateKeyOf).filter(Boolean),
        reason: r.reason || '',
      },
      monthOffset: 0,
      loadedMonths: {},
      holidayEvents: {},
      holidaysLoading: 0,
      errors: {},
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    organizationId() {
      return this.account?.organization?.id ?? this.account?.organizationId;
    },
    isEdit() {
      return this.mode === 'edit';
    },
    isSelf() {
      return !!this.employeeId && String(this.employeeId) === String(this.account?.id);
    },
    employeeLocked() {
      return this.isEdit || !!this.employeeId;
    },
    title() {
      if (this.isEdit) return 'Edit leave request';
      return this.isSelf ? 'Request leave' : 'New leave request';
    },
    subtitle() {
      if (this.isEdit) return 'The request stays pending until it is approved or rejected.';
      if (this.isSelf) return 'Your request goes to an administrator for approval.';
      return 'The request stays pending until it is approved or rejected.';
    },
    ethiopic() {
      return this.account?.organization?.calendarSystem === CalendarSystem.ETHIOPIC;
    },
    saving() {
      return this.$store.getters.leaveRequest.isLoading;
    },
    leaveTypes() {
      return (this.$store.getters.leaveTypes.data ?? []).filter((type) => type.isActive !== false || type.id === this.form.leaveTypeId);
    },
    typesLoading() {
      return this.$store.getters.leaveTypes.isLoading;
    },
    selectedType() {
      return this.leaveTypes.find((type) => type.id === this.form.leaveTypeId) ?? this.request?.leaveType ?? null;
    },
    balances() {
      const id = this.employee?.id;
      return (this.$store.getters.leaveBalances.data ?? []).filter((balance) => balance && balance.employeeId === id);
    },
    balancesLoading() {
      return this.$store.getters.leaveBalances.isLoading;
    },
    balance() {
      return this.balances.find((balance) => balance.leaveTypeId === this.form.leaveTypeId) ?? null;
    },
    policy() {
      return this.balance?.leaveType?.leavePolicy ?? this.selectedType?.leavePolicy ?? null;
    },
    typeInfo() {
      if (!this.employee || !this.form.leaveTypeId) return null;
      if (this.balancesLoading && !this.balances.length) return { tone: '', icon: 'pi-spin pi-spinner', text: 'Checking the balance' };
      if (!this.policy) return null;
      const info = leaveEligibility({ ...(this.balance || {}), leaveType: { ...(this.selectedType || {}), leavePolicy: this.policy } }, this.employee);
      if (!info.eligible) return { tone: 'warn', icon: 'pi-exclamation-triangle', text: info.note };
      if (info.tracked) {
        if (!this.balance) return { tone: '', icon: 'pi-info-circle', text: 'No balance is recorded for this leave type yet' };
        const available = Number(this.balance.available ?? 0);
        return { tone: available > 0 ? 'ok' : 'warn', icon: 'pi-wallet', text: `${formatDays(available)} available, ${info.note}` };
      }
      return { tone: '', icon: 'pi-info-circle', text: info.value ? `${info.value} ${info.note}` : info.note };
    },
    requestedDays() {
      return this.form.halfDay ? 0.5 : Number(this.form.days) || 0;
    },
    overBalance() {
      if (!this.policy?.balanceTracked || !this.balance) return 0;
      const over = this.requestedDays - Number(this.balance.available ?? 0);
      return over > 0 ? Math.round(over * 10) / 10 : 0;
    },
    holidayList() {
      return Object.values(this.holidayEvents);
    },
    holidayNames() {
      const names = {};
      this.holidayList.forEach((event) => {
        names[event.start] = names[event.start] ? `${names[event.start]}, ${event.title}` : event.title;
      });
      return names;
    },
    rules() {
      return {
        holidays: new Set(this.holidayList.map((event) => event.start)),
        skip: new Set(this.form.skipDays),
        include: new Set(this.form.includeDays),
      };
    },
    endDate() {
      return this.form.startDate ? leaveEndDate(this.form.startDate, this.requestedDays, this.rules) : '';
    },
    view() {
      return monthView(this.form.startDate || todayKey(), this.monthOffset, this.ethiopic);
    },
    cells() {
      const start = this.form.startDate;
      const end = this.endDate;
      const today = todayKey();
      const editable = !!start && !this.form.halfDay;
      return this.view.cells.map((cell) => {
        const inRange = !!start && cell.key >= start && cell.key <= end;
        const holiday = this.rules.holidays.has(cell.key);
        const skipped = inRange && this.rules.skip.has(cell.key);
        const included = inRange && holiday && this.rules.include.has(cell.key);
        const counted = inRange && countsAsLeave(cell.key, this.rules);
        const name = this.holidayNames[cell.key];
        let title = name || '';
        if (cell.inMonth) {
          if (!inRange) title = name ? `${name}. Click to start the leave here.` : 'Start the leave on this day';
          else if (editable && cell.key !== start) {
            if (holiday) title = included ? `${name}. Counted as leave, click to leave it out.` : `${name}. Not counted, click to count it.`;
            else title = skipped ? 'Skipped, click to count this day' : 'Click to skip this day';
          }
        }
        return { ...cell, inRange, holiday, skipped, included, counted, isToday: cell.key === today, title };
      });
    },
    neededMonths() {
      const months = new Set(monthsBetween(toDateKey(this.view.first), toDateKey(this.view.last)));
      if (this.form.startDate) monthsBetween(this.form.startDate, addDaysToKey(this.endDate || this.form.startDate, 7)).forEach((month) => months.add(month));
      return [...months].sort();
    },
    rangeLabel() {
      const start = this.$filters.getShortDate(this.form.startDate);
      return this.endDate && this.endDate !== this.form.startDate ? `${start} to ${this.$filters.getShortDate(this.endDate)}` : start;
    },
    lengthLabel() {
      if (this.form.halfDay) return `Half day, ${this.form.halfDaySlot === 'pm' ? 'afternoon' : 'morning'}`;
      const notCounted = this.cells.length ? this.rangeKeys().filter((key) => !countsAsLeave(key, this.rules)).length : 0;
      const days = formatDays(this.requestedDays);
      return notCounted ? `${days} of leave, ${formatDays(notCounted)} in between not counted` : `${days} of leave`;
    },
  },
  watch: {
    neededMonths: {
      handler(months) {
        this.loadMonths(months);
      },
      immediate: true,
    },
    'form.startDate'(value, previous) {
      if (value === previous) return;
      this.monthOffset = 0;
      this.form.skipDays = [];
      this.form.includeDays = [];
      if (this.errors.startDate) this.errors = { ...this.errors, startDate: null };
    },
    'form.leaveTypeId'() {
      if (this.errors.leaveTypeId) this.errors = { ...this.errors, leaveTypeId: null };
    },
    employee(value) {
      if (this.errors.employee) this.errors = { ...this.errors, employee: null };
      if (value?.id) this.loadBalances();
    },
  },
  methods: {
    formatDays,
    close() {
      if (!this.saving) this.$emit('close');
    },
    rangeKeys() {
      if (!this.form.startDate || !this.endDate) return [];
      const keys = [];
      for (let key = this.form.startDate; key <= this.endDate && keys.length < 1000; key = addDaysToKey(key, 1)) keys.push(key);
      return keys;
    },
    async loadEmployee() {
      if (this.employee || !this.employeeId) return;
      const cached = this.$store.getters.employee.data;
      if (cached && String(cached.id) === String(this.employeeId)) {
        this.employee = cached;
        return;
      }
      const res = await Employee.getEmployees({ id: this.employeeId, organizationId: this.organizationId }).catch(() => null);
      const found = res?.data?.status && Array.isArray(res.data.data) ? res.data.data[0] : null;
      if (found) this.employee = found;
    },
    loadBalances() {
      this.$store.dispatch(GET_LEAVE_BALANCES, { employeeId: this.employee.id, organizationId: this.organizationId });
    },
    async loadMonths(months) {
      const missing = months.filter((month) => !this.loadedMonths[month]);
      if (!missing.length || !this.organizationId) return;
      missing.forEach((month) => {
        this.loadedMonths[month] = true;
      });
      this.holidaysLoading += 1;
      try {
        const batches = await Promise.all(
          missing.map((month) => {
            const [year, m] = month.split('-').map(Number);
            return fetchHolidaysBetween(this.organizationId, new Date(year, m - 1, 1), new Date(year, m, 0));
          })
        );
        const next = { ...this.holidayEvents };
        batches.flat().forEach((event) => {
          next[`${event.id}|${event.start}`] = event;
        });
        this.holidayEvents = next;
      } finally {
        this.holidaysLoading -= 1;
      }
    },
    setHalfDay() {
      this.form.halfDay = true;
      this.form.skipDays = [];
      this.form.includeDays = [];
    },
    toggleDay(cell) {
      if (!cell.inMonth) return;
      if (!cell.inRange) {
        this.form.startDate = cell.key;
        return;
      }
      if (cell.key === this.form.startDate || this.form.halfDay) return;
      const listName = cell.holiday ? 'includeDays' : 'skipDays';
      const list = this.form[listName];
      this.form[listName] = list.includes(cell.key) ? list.filter((key) => key !== cell.key) : [...list, cell.key];
    },
    cellClass(cell) {
      return [
        'lv-day',
        {
          out: !cell.inMonth,
          today: cell.isToday,
          counted: cell.counted,
          start: cell.inRange && cell.key === this.form.startDate,
          end: cell.inRange && cell.key === this.endDate,
          skipped: cell.skipped,
          off: cell.inRange && !cell.counted && !cell.skipped,
          holiday: cell.holiday,
        },
      ];
    },
    validate() {
      const errors = {};
      if (!this.employee?.id) errors.employee = 'Choose who the leave is for';
      if (!this.form.leaveTypeId) errors.leaveTypeId = 'Choose a leave type';
      if (!this.form.startDate) errors.startDate = 'Pick the first day of leave';
      if (!this.form.halfDay && (!Number.isInteger(Number(this.form.days)) || Number(this.form.days) < 1)) errors.days = 'Enter a whole number of days, 1 or more';
      this.errors = errors;
      return !Object.keys(errors).length;
    },
    async save() {
      if (!this.validate()) return;
      const inRange = (key) => key >= this.form.startDate && key <= this.endDate;
      const body = {
        leaveTypeId: this.form.leaveTypeId,
        startDate: this.form.startDate,
        days: this.requestedDays,
        unit: this.form.halfDay ? 'halfDay' : 'day',
        halfDaySlot: this.form.halfDay ? this.form.halfDaySlot : null,
        skipDays: this.form.halfDay ? [] : this.form.skipDays.filter(inRange),
        includeDays: this.form.halfDay ? [] : this.form.includeDays.filter(inRange),
        reason: this.form.reason.trim() || null,
        employeeId: this.employee.id,
        organizationId: this.organizationId,
      };
      const ok = this.isEdit
        ? await this.$store.dispatch(EDIT_LEAVE_REQUEST, { id: this.request.id, ...body })
        : await this.$store.dispatch(REGISTER_LEAVE_REQUEST, body);
      if (!ok) return;
      this.$toast.success(this.isEdit ? 'Leave request updated' : this.isSelf ? 'Leave requested' : 'Leave request submitted', TOAST_GLOBAL_CONFIG);
      this.$emit('saved');
      this.$emit('close');
    },
    onKey(event) {
      if (event.key === 'Escape') this.close();
    },
  },
  mounted() {
    this.$store.dispatch(GET_LEAVE_TYPES, { organizationId: this.organizationId });
    if (this.employee?.id) this.loadBalances();
    else this.loadEmployee();
    document.addEventListener('keydown', this.onKey);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKey);
  },
};
</script>
